import { useState, useEffect, useRef, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { QRCodeSVG } from "qrcode.react";
import { Html5Qrcode } from "html5-qrcode";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SITE_URL = process.env.REACT_APP_BACKEND_URL;

function formatTime(d) {
  return d.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit", hour12: false });
}
function formatDate(d) {
  return d.toLocaleDateString("en-US", { weekday: "long", month: "long", day: "numeric" });
}

const STYLE = `
  @import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@400;500;700;800&display=swap');
  * { box-sizing: border-box; margin: 0; padding: 0; }
  html, body, #root { height: 100%; overflow: hidden; }

  .kiosk {
    font-family: 'Space Grotesk', sans-serif;
    height: 100vh; width: 100vw;
    display: flex; flex-direction: column;
    align-items: center; justify-content: center;
    background: #09090b; color: #fafafa;
    position: relative; overflow: hidden;
    user-select: none;
  }
  .kiosk::before {
    content: '';
    position: absolute;
    top: -30%; left: 50%; transform: translateX(-50%);
    width: 600px; height: 600px;
    background: radial-gradient(circle, rgba(255,214,10,0.06) 0%, transparent 70%);
    pointer-events: none;
  }
  .kiosk > * { position: relative; z-index: 1; }

  .k-topbar {
    position: absolute; top: 0; left: 0; right: 0;
    display: flex; justify-content: space-between; align-items: center;
    padding: 28px 36px;
  }
  .k-logo { font-size: 18px; font-weight: 800; letter-spacing: 3px; text-transform: uppercase; color: #52525b; }
  .k-time { font-size: 14px; color: #3f3f46; letter-spacing: 1px; font-variant-numeric: tabular-nums; }

  .k-main {
    display: flex; flex-direction: column; align-items: center; text-align: center;
    gap: 32px; animation: kFadeIn 0.8s ease-out both;
    width: 100%; max-width: 480px; padding: 0 24px;
  }
  .k-welcome { font-size: 42px; font-weight: 800; letter-spacing: -1.5px; line-height: 1.2; }
  .k-welcome span {
    background: linear-gradient(135deg, #FFD60A, #FF6B2B);
    -webkit-background-clip: text; -webkit-text-fill-color: transparent; background-clip: text;
  }
  .k-subtitle { font-size: 16px; color: #52525b; margin-top: 6px; letter-spacing: 1px; }

  /* Mode selector */
  .k-modes {
    display: flex; gap: 12px; width: 100%;
  }
  .k-mode-btn {
    flex: 1; font-family: 'Space Grotesk', sans-serif;
    padding: 14px 16px; font-size: 14px; font-weight: 700;
    border: 1px solid #27272a; background: transparent; color: #71717a;
    cursor: pointer; transition: all 0.3s ease;
    letter-spacing: 1px; text-transform: uppercase;
  }
  .k-mode-btn.active { background: #fafafa; color: #09090b; border-color: #fafafa; }
  .k-mode-btn:hover:not(.active) { border-color: #52525b; color: #a1a1aa; }

  /* QR display section */
  .k-qr-section { display: flex; flex-direction: column; align-items: center; gap: 14px; }
  .k-qr-frame {
    padding: 20px; background: #fafafa; border-radius: 16px;
    box-shadow: 0 0 60px rgba(255,214,10,0.08), 0 20px 40px rgba(0,0,0,0.3);
    animation: kPulseGlow 4s ease-in-out infinite;
  }
  .k-qr-hint { font-size: 13px; color: #3f3f46; max-width: 300px; line-height: 1.5; }

  /* Scanner section */
  .k-scanner-section { display: flex; flex-direction: column; align-items: center; gap: 16px; width: 100%; }
  .k-scanner-box {
    width: 280px; height: 280px; border-radius: 16px; overflow: hidden;
    border: 2px solid #27272a; position: relative;
  }
  .k-scanner-box video { width: 100%; height: 100%; object-fit: cover; }
  .k-scanner-label { font-size: 13px; color: #52525b; }
  .k-scanner-or { font-size: 12px; color: #3f3f46; letter-spacing: 3px; text-transform: uppercase; margin: 4px 0; }

  /* Code entry */
  .k-code-section { display: flex; flex-direction: column; align-items: center; gap: 12px; width: 100%; }
  .k-code-input {
    font-family: 'Space Grotesk', sans-serif;
    width: 100%; max-width: 340px; padding: 14px 20px;
    font-size: 16px; text-align: center; letter-spacing: 2px;
    background: #18181b; border: 1px solid #27272a; color: #fafafa;
    outline: none; transition: border-color 0.3s;
  }
  .k-code-input:focus { border-color: #FFD60A; }
  .k-code-input::placeholder { color: #3f3f46; letter-spacing: 1px; }
  .k-code-submit {
    font-family: 'Space Grotesk', sans-serif;
    padding: 12px 40px; font-size: 14px; font-weight: 700;
    background: #fafafa; color: #09090b; border: none;
    cursor: pointer; letter-spacing: 2px; text-transform: uppercase;
    transition: all 0.3s;
  }
  .k-code-submit:hover { background: #FFD60A; }
  .k-code-submit:disabled { opacity: 0.5; cursor: not-allowed; }

  /* Status */
  .k-status { font-size: 14px; color: #ef4444; margin-top: 8px; }
  .k-status.success { color: #22c55e; }

  /* Walk-in link */
  .k-walkin {
    position: absolute; bottom: 32px; text-align: center;
  }
  .k-walkin-btn {
    font-family: 'Space Grotesk', sans-serif;
    font-size: 13px; font-weight: 600; color: #3f3f46;
    background: transparent; border: 1px solid #1c1c1f;
    padding: 10px 28px; cursor: pointer;
    letter-spacing: 1px; transition: all 0.3s;
  }
  .k-walkin-btn:hover { background: rgba(255,255,255,0.05); border-color: #27272a; color: #71717a; }

  @keyframes kFadeIn { from { opacity: 0; transform: translateY(20px); } to { opacity: 1; transform: translateY(0); } }
  @keyframes kPulseGlow {
    0%, 100% { box-shadow: 0 0 40px rgba(255,214,10,0.06), 0 20px 40px rgba(0,0,0,0.3); }
    50% { box-shadow: 0 0 60px rgba(255,214,10,0.12), 0 20px 40px rgba(0,0,0,0.3); }
  }
`;

export default function KioskCheckin() {
  const [time, setTime] = useState(new Date());
  const [mode, setMode] = useState("scan");
  const [code, setCode] = useState("");
  const [status, setStatus] = useState(null);
  const [scanning, setScanning] = useState(false);
  const [cameraOpen, setCameraOpen] = useState(false);
  const scannerRef = useRef(null);
  const html5QrRef = useRef(null);
  const navigate = useNavigate();

  useEffect(() => {
    const tick = setInterval(() => setTime(new Date()), 1000);
    return () => clearInterval(tick);
  }, []);

  useEffect(() => {
    const id = "kiosk-styles";
    if (!document.getElementById(id)) {
      const s = document.createElement("style");
      s.id = id; s.textContent = STYLE;
      document.head.appendChild(s);
    }
  }, []);

  const handleCheckin = useCallback(async (guestId) => {
    setStatus({ type: "loading", msg: "Checking in..." });
    try {
      const res = await fetch(`${API}/guests/${guestId}/check-in`, { method: "PUT" });
      if (!res.ok) {
        const err = await res.json();
        setStatus({ type: "error", msg: err.detail || "Invalid code" });
        return;
      }
      const data = await res.json();
      setStatus({
        type: "success",
        msg: data.already_checked_in
          ? `${data.guest_name}, you're already checked in!`
          : `Welcome, ${data.guest_name}! ${data.host_name ? data.host_name + " has been notified." : ""}`
      });
      setTimeout(() => { setStatus(null); setCode(""); setMode("scan"); setCameraOpen(false); }, 8000);
    } catch {
      setStatus({ type: "error", msg: "Check-in failed. Please try again." });
    }
  }, []);

  const extractGuestId = useCallback((input) => {
    // Accept full URL or just the ID
    const urlMatch = input.match(/guest-checkin\/([a-f0-9-]{36})/);
    if (urlMatch) return urlMatch[1];
    const idMatch = input.match(/^[a-f0-9-]{36}$/);
    if (idMatch) return idMatch[0];
    return null;
  }, []);

  const handleCodeSubmit = useCallback(() => {
    const guestId = extractGuestId(code.trim());
    if (!guestId) {
      setStatus({ type: "error", msg: "Invalid check-in code" });
      return;
    }
    handleCheckin(guestId);
  }, [code, extractGuestId, handleCheckin]);

  // QR Scanner — only starts when user taps
  const startScanner = useCallback(() => {
    setCameraOpen(true);
    // Small delay to let the DOM element render
    setTimeout(() => {
      const el = document.getElementById("kiosk-scanner");
      if (!el) return;
      const qr = new Html5Qrcode("kiosk-scanner");
      html5QrRef.current = qr;
      setScanning(true);
      qr.start(
        { facingMode: "environment" },
        { fps: 10, qrbox: { width: 220, height: 220 } },
        (decodedText) => {
          const guestId = extractGuestId(decodedText);
          if (guestId) {
            qr.stop().catch(() => {});
            html5QrRef.current = null;
            setScanning(false);
            setCameraOpen(false);
            handleCheckin(guestId);
          }
        },
        () => {}
      ).catch(() => { setScanning(false); setCameraOpen(false); });
    }, 100);
  }, [extractGuestId, handleCheckin]);

  const stopScanner = useCallback(() => {
    if (html5QrRef.current) {
      try {
        const state = html5QrRef.current.getState?.();
        if (state === 2 || state === undefined) {
          html5QrRef.current.stop().catch(() => {});
        }
      } catch {}
      html5QrRef.current = null;
    }
    setScanning(false);
    setCameraOpen(false);
  }, []);

  // Cleanup on mode change
  useEffect(() => {
    if (mode !== "scan") stopScanner();
  }, [mode, stopScanner]);

  return (
    <div className="kiosk" data-testid="kiosk-checkin-screen">
      <div className="k-topbar">
        <div className="k-logo" data-testid="kiosk-logo">The Rig</div>
        <div className="k-time" data-testid="kiosk-clock">{formatDate(time)} &middot; {formatTime(time)}</div>
      </div>

      <div className="k-main">
        <div>
          <div className="k-welcome" data-testid="kiosk-welcome">Welcome to <span>The Rig</span></div>
          <div className="k-subtitle">Guest Check-in</div>
        </div>

        {/* Mode tabs */}
        <div className="k-modes" data-testid="kiosk-modes">
          <button className={`k-mode-btn ${mode === "scan" ? "active" : ""}`} onClick={() => { setMode("scan"); setStatus(null); }} data-testid="mode-scan-btn">
            Scan QR
          </button>
          <button className={`k-mode-btn ${mode === "code" ? "active" : ""}`} onClick={() => { setMode("code"); setStatus(null); }} data-testid="mode-code-btn">
            Enter Code
          </button>
          <button className={`k-mode-btn ${mode === "register" ? "active" : ""}`} onClick={() => { setMode("register"); setStatus(null); }} data-testid="mode-register-btn">
            New Guest
          </button>
        </div>

        {/* Scan mode */}
        {mode === "scan" && (
          <div className="k-scanner-section" data-testid="scan-section">
            {!cameraOpen ? (
              <>
                <button className="k-code-submit" onClick={startScanner} data-testid="tap-to-scan-btn" style={{ padding: "20px 48px", fontSize: "16px" }}>
                  Tap to Scan QR Code
                </button>
                <div className="k-scanner-label">Open the camera to scan your check-in QR</div>
              </>
            ) : (
              <>
                <div className="k-scanner-box" id="kiosk-scanner" ref={scannerRef} />
                <div className="k-scanner-label">
                  {scanning ? "Point your QR code at the camera" : "Starting camera..."}
                </div>
                <button className="k-mode-btn" onClick={stopScanner} data-testid="close-camera-btn" style={{ marginTop: "8px", fontSize: "12px" }}>
                  Close Camera
                </button>
              </>
            )}
          </div>
        )}

        {/* Code entry mode */}
        {mode === "code" && (
          <div className="k-code-section" data-testid="code-section">
            <p className="k-scanner-label">Enter the check-in code from your email</p>
            <input
              className="k-code-input"
              type="text"
              placeholder="Paste check-in code..."
              value={code}
              onChange={e => setCode(e.target.value)}
              onKeyDown={e => { if (e.key === "Enter") handleCodeSubmit(); }}
              autoFocus
              data-testid="checkin-code-input"
            />
            <button
              className="k-code-submit"
              onClick={handleCodeSubmit}
              disabled={!code.trim()}
              data-testid="checkin-code-submit"
            >
              Check In
            </button>
          </div>
        )}

        {/* Register mode */}
        {mode === "register" && (
          <div className="k-qr-section" data-testid="register-section">
            <div className="k-qr-frame" data-testid="kiosk-qr-code">
              <QRCodeSVG
                value={`${SITE_URL}/guest-register`}
                size={180}
                bgColor="#fafafa"
                fgColor="#09090b"
                level="M"
              />
            </div>
            <div className="k-qr-hint">Scan to register on your phone, or</div>
            <button
              className="k-code-submit"
              onClick={() => navigate("/guest-register")}
              data-testid="kiosk-register-btn"
            >
              Register Here
            </button>
          </div>
        )}

        {/* Status message */}
        {status && (
          <div className={`k-status ${status.type}`} data-testid="kiosk-status">
            {status.msg}
          </div>
        )}
      </div>

      {/* Bottom link */}
      <div className="k-walkin">
        <button className="k-walkin-btn" onClick={() => navigate("/guest-register")} data-testid="kiosk-walkin-btn">
          First time? Register as guest
        </button>
      </div>
    </div>
  );
}
